import axios from 'axios';
import { API_BASE_URL } from './config';

// Interfaces
export interface SyncDevice {
  _id: string;
  name: string;
  deviceType: 'smartphone' | 'tablet' | 'laptop';
  platform: 'ios' | 'android' | 'windows' | 'macos';
  userId: string;
  userName: string;
  status: 'online' | 'offline' | 'syncing' | 'error';
  lastSync: string;
  batteryLevel?: number;
  storageUsed: number;
  storageTotal: number;
  connectionType: 'wifi' | 'cellular' | 'offline';
  signalStrength?: number;
  appVersion: string;
  osVersion: string;
  syncSettings: {
    autoSync: boolean;
    syncOnWifiOnly: boolean;
    syncInterval: number; // minutes
    syncPhotos: boolean;
    syncDocuments: boolean;
    syncProjects: boolean;
    maxFileSize: number; // MB
  };
  location?: {
    latitude: number;
    longitude: number;
    address: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface SyncJob {
  _id: string;
  deviceId: string;
  deviceName: string;
  type: 'upload' | 'download' | 'bidirectional';
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  dataType: 'photos' | 'documents' | 'projects' | 'all';
  totalFiles: number;
  processedFiles: number;
  failedFiles: number;
  totalSize: number; // bytes
  processedSize: number; // bytes
  speed?: number; // bytes per second
  estimatedTimeRemaining?: number; // seconds
  startedAt?: string;
  completedAt?: string;
  error?: string;
  conflicts: SyncConflict[];
  createdAt: string;
  updatedAt: string;
}

export interface SyncConflict {
  _id: string;
  jobId: string;
  fileId: string;
  fileName: string;
  conflictType: 'version' | 'deletion' | 'permission';
  localVersion: {
    modifiedAt: string;
    size: number;
    checksum: string;
  };
  remoteVersion: {
    modifiedAt: string;
    size: number;
    checksum: string;
  };
  resolution?: 'keep-local' | 'keep-remote' | 'merge' | 'skip';
  resolvedAt?: string;
  resolvedBy?: string;
  createdAt: string;
}

export interface SyncStats {
  totalDevices: number;
  onlineDevices: number;
  syncingDevices: number;
  totalJobs: number;
  activeJobs: number;
  completedJobs: number;
  failedJobs: number;
  totalDataTransferred: number; // bytes
  averageSpeed: number; // bytes per second
  conflictsResolved: number;
  pendingConflicts: number;
  syncSuccessRate: number; // percentage
  lastSyncTime: string;
}

export interface SyncFilters {
  deviceId?: string;
  status?: string;
  type?: string;
  dataType?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface SyncSettings {
  _id: string;
  userId: string;
  globalSettings: {
    autoSync: boolean;
    syncInterval: number;
    maxConcurrentJobs: number;
    retryAttempts: number;
    compressionEnabled: boolean;
    encryptionEnabled: boolean;
  };
  deviceSettings: {
    [deviceId: string]: {
      enabled: boolean;
      priority: number;
      customSettings: any;
    };
  };
  dataTypeSettings: {
    photos: {
      enabled: boolean;
      maxFileSize: number;
      compressionLevel: number;
    };
    documents: {
      enabled: boolean;
      maxFileSize: number;
      allowedTypes: string[];
    };
    projects: {
      enabled: boolean;
      syncFrequency: number;
    };
  };
  createdAt: string;
  updatedAt: string;
}

// Axios instance with auth token
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Mobile Sync Service
class MobileSyncService {
  // Device Management
  async getDevices(): Promise<SyncDevice[]> {
    const response = await api.get('/sync/devices');
    return response.data;
  }

  async getDevice(id: string): Promise<SyncDevice> {
    const response = await api.get(`/sync/devices/${id}`);
    return response.data;
  }

  async registerDevice(device: Omit<SyncDevice, '_id' | 'createdAt' | 'updatedAt'>): Promise<SyncDevice> {
    const response = await api.post('/sync/devices', device);
    return response.data;
  }

  async updateDevice(id: string, device: Partial<SyncDevice>): Promise<SyncDevice> {
    const response = await api.put(`/sync/devices/${id}`, device);
    return response.data;
  }

  async deleteDevice(id: string): Promise<void> {
    await api.delete(`/sync/devices/${id}`);
  }

  async updateDeviceStatus(id: string, status: SyncDevice['status']): Promise<SyncDevice> {
    const response = await api.patch(`/sync/devices/${id}/status`, { status });
    return response.data;
  }

  // Sync Job Management
  async getSyncJobs(filters?: SyncFilters): Promise<SyncJob[]> {
    const response = await api.get('/sync/jobs', { params: filters });
    return response.data;
  }

  async getSyncJob(id: string): Promise<SyncJob> {
    const response = await api.get(`/sync/jobs/${id}`);
    return response.data;
  }

  async createSyncJob(job: Omit<SyncJob, '_id' | 'createdAt' | 'updatedAt' | 'conflicts'>): Promise<SyncJob> {
    const response = await api.post('/sync/jobs', job);
    return response.data;
  }

  async startSyncJob(id: string): Promise<SyncJob> {
    const response = await api.post(`/sync/jobs/${id}/start`);
    return response.data;
  }

  async pauseSyncJob(id: string): Promise<SyncJob> {
    const response = await api.post(`/sync/jobs/${id}/pause`);
    return response.data;
  }

  async resumeSyncJob(id: string): Promise<SyncJob> {
    const response = await api.post(`/sync/jobs/${id}/resume`);
    return response.data;
  }

  async cancelSyncJob(id: string): Promise<SyncJob> {
    const response = await api.post(`/sync/jobs/${id}/cancel`);
    return response.data;
  }

  async deleteSyncJob(id: string): Promise<void> {
    await api.delete(`/sync/jobs/${id}`);
  }

  // Conflict Management
  async getConflicts(jobId?: string): Promise<SyncConflict[]> {
    const params = jobId ? { jobId } : {};
    const response = await api.get('/sync/conflicts', { params });
    return response.data;
  }

  async resolveConflict(conflictId: string, resolution: SyncConflict['resolution']): Promise<SyncConflict> {
    const response = await api.patch(`/sync/conflicts/${conflictId}/resolve`, { resolution });
    return response.data;
  }

  async bulkResolveConflicts(conflictIds: string[], resolution: SyncConflict['resolution']): Promise<void> {
    await api.patch('/sync/conflicts/bulk-resolve', { conflictIds, resolution });
  }

  // Settings Management
  async getSyncSettings(): Promise<SyncSettings> {
    const response = await api.get('/sync/settings');
    return response.data;
  }

  async updateSyncSettings(settings: Partial<SyncSettings>): Promise<SyncSettings> {
    const response = await api.put('/sync/settings', settings);
    return response.data;
  }

  async updateDeviceSettings(deviceId: string, settings: any): Promise<SyncSettings> {
    const response = await api.patch(`/sync/settings/device/${deviceId}`, settings);
    return response.data;
  }

  // Statistics and Monitoring
  async getStats(): Promise<SyncStats> {
    const response = await api.get('/sync/stats');
    return response.data;
  }

  async getDeviceStats(deviceId: string): Promise<any> {
    const response = await api.get(`/sync/devices/${deviceId}/stats`);
    return response.data;
  }

  async getSyncHistory(deviceId?: string, limit: number = 50): Promise<SyncJob[]> {
    const params: any = { limit };
    if (deviceId) params.deviceId = deviceId;
    
    const response = await api.get('/sync/history', { params });
    return response.data;
  }

  // Manual Sync Operations
  async triggerSync(deviceId: string, dataType?: string): Promise<SyncJob> {
    const response = await api.post(`/sync/devices/${deviceId}/sync`, { dataType });
    return response.data;
  }

  async triggerFullSync(deviceId: string): Promise<SyncJob> {
    const response = await api.post(`/sync/devices/${deviceId}/full-sync`);
    return response.data;
  }

  // Bulk Operations
  async bulkSync(deviceIds: string[], dataType?: string): Promise<SyncJob[]> {
    const response = await api.post('/sync/bulk-sync', { deviceIds, dataType });
    return response.data;
  }

  async bulkUpdateDeviceSettings(deviceIds: string[], settings: any): Promise<void> {
    await api.patch('/sync/bulk-update-settings', { deviceIds, settings });
  }

  // Health Check
  async checkSyncHealth(): Promise<any> {
    const response = await api.get('/sync/health');
    return response.data;
  }

  async testDeviceConnection(deviceId: string): Promise<any> {
    const response = await api.post(`/sync/devices/${deviceId}/test-connection`);
    return response.data;
  }

  // Export and Reports
  async exportSyncReport(filters?: SyncFilters, format: 'csv' | 'excel' | 'pdf' = 'csv'): Promise<Blob> {
    const response = await api.get('/sync/export', {
      params: { ...filters, format },
      responseType: 'blob',
    });
    return response.data;
  }
}

export const mobileSyncService = new MobileSyncService();
export default mobileSyncService;